import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  ArrowLeft, Loader2, Edit, FileText, Truck,
  CheckCircle2, XCircle, AlertTriangle, TrendingUp, Link2, Activity, Download, Wrench,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { soTermChips, SO_TERM_LABELS, type SoTermConflict } from './soTerms';
import { AdditionalDiscountSplitCard } from './AdditionalDiscountSplitCard';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  confirmed: 'bg-blue-50 text-blue-700',
  processing: 'bg-yellow-50 text-yellow-700',
  partial_shipped: 'bg-orange-50 text-orange-700',
  shipped: 'bg-indigo-50 text-indigo-700',
  delivered: 'bg-green-50 text-green-700',
  cancelled: 'bg-red-50 text-red-700',
};

const FULFILLMENT_LABELS: Record<string, { label: string; color: string }> = {
  in_stock: { label: 'In Stock', color: 'bg-green-50 text-green-700' },
  procure: { label: 'Procure', color: 'bg-blue-50 text-blue-700' },
  manufacture: { label: 'Manufacture', color: 'bg-purple-50 text-purple-700' },
  service: { label: 'Service', color: 'bg-amber-50 text-amber-700' },
};

function formatStatus(s: string) {
  return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Dealer billing (2026-08-31), spec §6 / decision 8.
//
// 🔒 `SalesOrder.total` IS NOT REVENUE on a dealer order. The customer's money
// goes into the DEALER's bank account; only `dealerBilledValue` settles anything
// with Sun Stellar. So this band never prints `total` — Accounts reads one
// sentence, "₹1,00,165 from Garg Sanitary", and knows who to invoice and who to
// chase. Production reads the other half: the goods go to the CUSTOMER, who
// never touches the dealer's price.
//
// 🔒 The numbers are NOT computed here. `buildDealerOrderView`
// (backend/src/modules/sun-stellar-commerce/dealer-billing/dealer-order-view.ts)
// is the single source of truth; this renders what the payload carries. A second
// implementation of the arithmetic is a second answer.
//
// 🔒 `channelMode !== 'dealer'` renders NOTHING — not an empty wrapper. A direct
// order's page is byte-identical to what it was before this band existed.
//
// 🔒 The field is `dealerSpread`, never `dealerMargin` — that name is on the
// COST_FIELDS deny list. The visible LABEL is "Dealer margin". Both deliberate.
export interface DealerOrderFields {
  channelMode?: string | null;
  billToPartyType?: string | null;
  dealer?: { name?: string | null; gstin?: string | null } | null;
  customerName?: string | null;
  // Carried on the order payload; declared so callers stay typed. 🔒 Neither
  // this nor `total` is rendered by the band — `revenueExpected` is the only
  // money this component prints, and the backend decides what it equals.
  dealerBilledValue?: number | string | null;
  contact?: { firstName?: string | null; lastName?: string | null } | null;
  account?: { name?: string | null } | null;
  revenueExpected?: number | string | null;
  revenueFrom?: string | null;
  dealerSpread?: number | string | null;
  dealerSpreadPct?: number | string | null;
}

// Signed rupees. 🔒 A negative spread is shown as negative — never clamped with
// Math.abs, never hidden. It is the loudest thing on a mispriced order.
function dealerINR(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const num = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(num)) return '—';
  const rounded = Math.round(num);
  return `${rounded < 0 ? '-' : ''}₹${Math.abs(rounded).toLocaleString('en-IN')}`;
}

export function SalesOrderDealerBand({ order }: { order: DealerOrderFields }) {
  if (order?.channelMode !== 'dealer') return null;

  const billToDealer = order.billToPartyType === 'dealer';
  const dealerName = order.dealer?.name || 'Unknown dealer';
  const customerName =
    order.customerName ||
    joinPersonName(order.contact?.firstName, order.contact?.lastName) ||
    order.account?.name ||
    'Customer';
  // The payload's `revenueFrom` wins; the local fallback exists only so the band
  // still reads correctly on an order served before the serializer was wired.
  const invoiceTo = order.revenueFrom || (billToDealer ? dealerName : customerName);

  const spreadNum = order.dealerSpread == null ? null : Number(order.dealerSpread);
  const negative = spreadNum != null && Number.isFinite(spreadNum) && spreadNum < 0;
  const pct = order.dealerSpreadPct == null ? null : Number(order.dealerSpreadPct);

  return (
    <div
      data-testid="dealer-band"
      className={`mb-6 rounded-xl border p-4 ${
        negative ? 'border-rose-300 bg-rose-50' : 'border-indigo-200 bg-indigo-50'
      }`}
    >
      <div
        className={`text-xs font-semibold uppercase tracking-wider ${
          negative ? 'text-rose-700' : 'text-indigo-700'
        }`}
      >
        {negative ? 'Revenue below the customer price' : 'Through dealer'}
        {' — '}
        {dealerName}
        {order.dealer?.gstin ? ` · GSTIN ${order.dealer.gstin}` : ''}
      </div>

      <p
        className={`mt-1 text-lg font-bold ${negative ? 'text-rose-800' : 'text-gray-900'}`}
      >
        {dealerINR(order.revenueExpected)} from {invoiceTo}
      </p>

      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs uppercase tracking-wider text-gray-500">Deliver to</dt>
          <dd className="font-medium text-gray-900">{customerName}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-gray-500">Invoice to</dt>
          <dd className="font-medium text-gray-900">{invoiceTo}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-gray-500">Revenue expected</dt>
          <dd className="font-medium text-gray-900">{dealerINR(order.revenueExpected)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-gray-500">Dealer margin</dt>
          <dd
            data-testid="dealer-band-spread"
            className={negative ? 'font-bold text-rose-700' : 'font-bold text-indigo-700'}
          >
            {dealerINR(order.dealerSpread)}
          </dd>
          {pct != null && Number.isFinite(pct) && (
            <dd className={`text-xs ${negative ? 'text-rose-600' : 'text-gray-500'}`}>{pct}%</dd>
          )}
        </div>
      </dl>

      {negative && (
        <p className="mt-2 text-sm text-rose-700">
          We would bill the dealer more than the customer pays. Usually this means the
          customer discount was approved without the dealer rate in view.
        </p>
      )}
    </div>
  );
}

export function SalesOrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [so, setSO] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [mappingItems, setMappingItems] = useState<any[]>([]);
  const [mappingLoading, setMappingLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/sales-orders/${id}`)
      .then(res => setSO(res.data.data))
      .catch(() => setError('Failed to load sales order'))
      .finally(() => setLoading(false));

    setMappingLoading(true);
    api.get(`/so-po-mapping/sales-orders/${id}`)
      .then(res => setMappingItems(res.data.data?.items || res.data.data || []))
      .catch(() => {}) // silently fail — mapping may not exist yet
      .finally(() => setMappingLoading(false));
  }, [id]);

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this sales order? This will trigger fulfillment processes.')) return;
    setConfirming(true);
    try {
      const res = await api.patch(`/sales-orders/${id}/confirm`);
      setSO(res.data.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this sales order?')) return;
    try {
      const res = await api.patch(`/sales-orders/${id}/status`, { status: 'cancelled' });
      setSO(res.data.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading sales order...</span>
      </div>
    );
  }

  if (error || !so) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-gray-400">
        <AlertTriangle size={40} className="mb-2" />
        <p className="text-sm">{error || 'Sales order not found'}</p>
        <button onClick={() => navigate('/sales-orders')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Back to list</button>
      </div>
    );
  }

  const fulfilledPct = Number(so.fulfilledQtyPct) || 0;
  const invoicedPct = Number(so.invoicedQtyPct) || 0;

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/sales-orders')} className="rounded-lg p-1.5 hover:bg-gray-100">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">{so.number}</h1>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[so.status] || STATUS_COLORS.draft}`}>
                {formatStatus(so.status)}
              </span>
              {so.isExport && (
                <span className="rounded bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">EXPORT</span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              Order Date: {formatDate(so.orderDate)}
              {so.deliveryDate && ` | Delivery: ${formatDate(so.deliveryDate)}`}
            </p>
            {/* CVG3-DISC (CVG2-WON carry-over) — the 3 close-won terms as ✓/✗
                chips, mirroring the mobile Orders tab (soTermChips): a null
                term renders no chip; legacy all-null orders render no row. */}
            {soTermChips(so).length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {soTermChips(so).map(c => (
                  <span
                    key={c.label}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      c.value ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {c.label} {c.value ? '✓' : '✗'}
                  </span>
                ))}
              </div>
            )}
            {/* T-045-W — rep-vs-proposal term conflicts, READ-ONLY on the web
                console by design (plan/spec T-045, Lane W, W3). Absent when
                `termConflicts` is absent or empty — never an empty-state
                panel, same rule as the chips above. No resolve control here:
                resolving belongs where the rep being overridden is standing
                (mobile, Lane C); a manager who must override already has
                T-027-B's audited terms-edit endpoint. Renders the server's
                own record only — never re-derives a conflict in the browser. */}
            {Array.isArray(so.termConflicts) && so.termConflicts.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {(so.termConflicts as SoTermConflict[]).map(c => (
                  <div
                    key={c.field}
                    className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700 ring-1 ring-amber-300"
                    title={`${SO_TERM_LABELS[c.field]}: she answered ${c.answered ? 'Yes' : 'No'}, the proposal implied ${c.derived ? 'Yes' : 'No'}. Her answer stands unless she resolves it.`}
                  >
                    <AlertTriangle size={12} className="shrink-0" />
                    <span>
                      <strong>{SO_TERM_LABELS[c.field]}</strong> disagreement — she answered{' '}
                      <strong>{c.answered ? 'Yes' : 'No'}</strong>, proposal implied{' '}
                      <strong>{c.derived ? 'Yes' : 'No'}</strong>. Order currently holds{' '}
                      <strong>{c.answered ? 'Yes' : 'No'}</strong>.
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {so.status === 'draft' && (
            <>
              <button onClick={() => navigate(`/sales-orders/${id}/edit`)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                <Edit size={14} />
                Edit
              </button>
              <button onClick={handleConfirm} disabled={confirming}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors disabled:opacity-50">
                {confirming ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Confirm Order
              </button>
            </>
          )}
          {so.status !== 'draft' && so.status !== 'cancelled' && (
            <>
              <button onClick={() => navigate(`/sales-orders/${id}/fulfil`)}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors">
                <Activity size={14} />
                Manage Fulfilment
              </button>
              <button
                onClick={() => navigate(`/sales-orders/${id}/fulfil?dispatchWithoutInvoice=1`)}
                className="flex items-center gap-1.5 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100 transition-colors"
                title="Dispatch items before invoicing (pre-invoice dispatch)"
              >
                <Activity size={14} />
                Dispatch w/o Invoice
              </button>
              <button
                onClick={() => navigate(`/dispatch-forms/new/${id}`)}
                className="flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                title="Build a dispatch form (one truck) from this order"
              >
                <Truck size={14} />
                Dispatch Form
              </button>
            </>
          )}
          <button onClick={() => navigate(`/sales-orders/${id}/margins`)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            <TrendingUp size={14} />
            View Margins
          </button>
          {so.status !== 'cancelled' && so.status !== 'delivered' && so.status !== 'draft' && (
            <button onClick={() => setShowInvoiceModal(true)}
              className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors">
              <FileText size={14} />
              Create Invoice
            </button>
          )}
          {so.status !== 'cancelled' && so.status !== 'delivered' && (
            <button onClick={handleCancel}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors">
              <XCircle size={14} />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Dealer billing band — renders NOTHING unless channelMode === 'dealer',
          so a direct order's page is exactly what it was. Sits between the
          header and the summary cards; nothing below it moved. */}
      <SalesOrderDealerBand order={so} />

      {/* Customer & Overview Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Customer */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</h3>
          {so.contact && (
            <p className="font-medium text-gray-900">{joinPersonName(so.contact.firstName, so.contact.lastName)}</p>
          )}
          {so.account && (
            <p className="text-sm text-gray-600">{so.account.name}</p>
          )}
          {so.buyerGstin && <p className="mt-1 text-xs text-gray-400">GSTIN: {so.buyerGstin}</p>}
          {so.isExport && so.exportCountry && (
            <p className="mt-1 text-xs text-gray-400">Export to: {so.exportCountry} | Terms: {so.deliveryTerms || 'N/A'}</p>
          )}
        </div>

        {/* Totals */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Total</h3>
          <p className="text-2xl font-bold text-gray-900">{formatINR(Number(so.total))}</p>
          <div className="mt-2 space-y-0.5 text-xs text-gray-500">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(Number(so.subtotal))}</span></div>
            <div className="flex justify-between"><span>Tax</span><span>{formatINR(Number(so.totalTax))}</span></div>
            {Number(so.discount) > 0 && (
              <div className="flex justify-between"><span>Discount</span><span>-{formatINR(Number(so.discount))}</span></div>
            )}
          </div>
          {so.currency !== 'INR' && (
            <p className="mt-2 text-xs text-gray-400">Currency: {so.currency} | Rate: {Number(so.exchangeRate)}</p>
          )}
        </div>

        {/* Fulfillment Progress */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Progress</h3>
          <div className="space-y-3">
            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-gray-600"><Truck size={12} /> Fulfilled</span>
                <span className="font-medium">{Math.round(fulfilledPct)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200">
                <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(fulfilledPct, 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-gray-600"><FileText size={12} /> Invoiced</span>
                <span className="font-medium">{Math.round(invoicedPct)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200">
                <div className="h-2 rounded-full bg-green-500 transition-all" style={{ width: `${Math.min(invoicedPct, 100)}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 2 plan 11 — X3 additional discount + A/B split. Renders nothing until a line is split-eligible. */}
      <AdditionalDiscountSplitCard salesOrderId={so.id} />

      {/* Addresses */}
      {(so.billingAddress || so.shippingAddress) && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {so.billingAddress && (so.billingAddress.line1 || so.billingAddress.city) && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Billing Address</h3>
              <div className="text-sm text-gray-700 space-y-0.5">
                {so.billingAddress.line1 && <p>{so.billingAddress.line1}</p>}
                {so.billingAddress.line2 && <p>{so.billingAddress.line2}</p>}
                <p>
                  {[so.billingAddress.city, so.billingAddress.state, so.billingAddress.pincode].filter(Boolean).join(', ')}
                </p>
                {so.billingAddress.country && so.billingAddress.country !== 'India' && <p>{so.billingAddress.country}</p>}
              </div>
            </div>
          )}
          {so.shippingAddress && (so.shippingAddress.line1 || so.shippingAddress.city) && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Shipping / Delivery Address</h3>
              <div className="text-sm text-gray-700 space-y-0.5">
                {so.shippingAddress.line1 && <p>{so.shippingAddress.line1}</p>}
                {so.shippingAddress.line2 && <p>{so.shippingAddress.line2}</p>}
                <p>
                  {[so.shippingAddress.city, so.shippingAddress.state, so.shippingAddress.pincode].filter(Boolean).join(', ')}
                </p>
                {so.shippingAddress.country && <p>{so.shippingAddress.country}</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Line Items Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-700">Line Items ({so.items?.length || 0})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">Product</th>
                <th className="px-4 py-2">HSN</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2">GST</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2 text-right">Fulfilled</th>
                <th className="px-4 py-2 text-right">Invoiced</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {so.items?.map((item: any) => {
                const source = FULFILLMENT_LABELS[item.fulfillmentSource] || FULFILLMENT_LABELS.in_stock;
                const qty = Number(item.quantity);
                const fulfilled = Number(item.fulfilledQty);
                const invoiced = Number(item.invoicedQty);
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{item.description}</p>
                      {item.product && <p className="text-xs text-gray-400">{item.product.sku}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.hsnSac || '-'}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{qty} {item.unit || ''}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatINR(Number(item.rate))}</td>
                    <td className="px-4 py-3 text-gray-600">{Number(item.gstRate)}%</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${source.color}`}>
                        {source.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={fulfilled >= qty ? 'text-green-600' : 'text-gray-600'}>
                        {fulfilled}/{qty}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={invoiced >= qty ? 'text-green-600' : 'text-gray-600'}>
                        {invoiced}/{qty}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(Number(item.amount))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Service Fulfillment — only for non-shipping service items (shipping uses shipment flow) */}
      {so.items?.some((it: any) => it.fulfillmentSource === 'service' && it.serviceCategory !== 'shipping') && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-white overflow-hidden">
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-3 flex items-center gap-2">
            <Wrench size={14} className="text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-900">Service Fulfillment</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {so.items.filter((it: any) => it.fulfillmentSource === 'service' && it.serviceCategory !== 'shipping').map((item: any) => {
              const qty = Number(item.quantity);
              const fulfilled = Number(item.fulfilledQty);
              const pctDone = qty > 0 ? Math.min(100, Math.round((fulfilled / qty) * 100)) : 0;
              const isDone = fulfilled >= qty;
              return (
                <div key={item.id} className="px-5 py-3 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.description}</p>
                    <p className="text-xs text-gray-500">
                      {item.serviceCategory || 'service'} · {fulfilled}/{qty} {item.unit || ''} complete ({pctDone}%)
                    </p>
                    <div className="mt-1 h-1.5 w-48 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-amber-500 transition-all" style={{ width: `${pctDone}%` }} />
                    </div>
                  </div>
                  {isDone ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                      <CheckCircle2 size={12} /> Completed
                    </span>
                  ) : (
                    <button
                      onClick={async () => {
                        const qtyRaw = window.prompt(`Complete how many ${item.unit || 'units'}? (pending: ${qty - fulfilled})`, String(qty - fulfilled));
                        if (!qtyRaw) return;
                        const completedQty = Number(qtyRaw);
                        if (!completedQty || completedQty <= 0) return;
                        const receiverName = window.prompt('Receiver name (optional):') || '';
                        const notes = window.prompt('Notes (optional):') || '';
                        try {
                          await api.post('/service-completions', {
                            salesOrderItemId: item.id,
                            completedQty,
                            receiverName: receiverName || null,
                            notes: notes || null,
                          });
                          window.location.reload();
                        } catch (e: any) {
                          alert(e?.response?.data?.error || 'Failed to log completion');
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                    >
                      <CheckCircle2 size={12} /> Mark Complete
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SO-PO Mapping */}
      {mappingItems.length > 0 && (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-200 bg-gray-50 px-5 py-3 flex items-center gap-2">
            <Link2 size={14} className="text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-700">SO-PO Mapping</h3>
          </div>
          {mappingLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={16} className="animate-spin text-[var(--saffron-600)]" />
              <span className="ml-2 text-xs text-gray-500">Loading mapping...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2">Item</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Delay</th>
                    <th className="px-4 py-2">PO Number</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {mappingItems.map((mi: any) => {
                    const delayBg = mi.delayHours < 24
                      ? 'bg-green-50 text-green-700'
                      : mi.delayHours < 48
                        ? 'bg-yellow-50 text-yellow-700'
                        : 'bg-red-50 text-red-700';
                    const statusBg = mi.status === 'mapped'
                      ? 'bg-green-50 text-green-700'
                      : mi.status === 'in_progress'
                        ? 'bg-yellow-50 text-yellow-700'
                        : 'bg-red-50 text-red-700';
                    const statusLabel = (mi.status as string).split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    return (
                      <tr key={mi.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{mi.itemDescription}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBg}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${delayBg}`}>
                            {(mi.delayHours ?? 0).toFixed(1)}h
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {mi.poId && mi.poNumber ? (
                            <button
                              onClick={() => navigate(`/purchase-orders/${mi.poId}`)}
                              className="text-[var(--ink-0)] hover:underline text-sm font-medium"
                            >
                              {mi.poNumber}
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {(so.notes || so.terms || so.internalNotes) && (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {so.notes && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Notes</h3>
              <p className="text-sm text-gray-700 whitespace-pre-line">{so.notes}</p>
            </div>
          )}
          {so.terms && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Terms & Conditions</h3>
              <p className="text-sm text-gray-700 whitespace-pre-line">{so.terms}</p>
            </div>
          )}
          {so.internalNotes && (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-5">
              <h3 className="mb-2 text-xs font-semibold text-yellow-700 uppercase tracking-wider">Internal Notes</h3>
              <p className="text-sm text-yellow-800 whitespace-pre-line">{so.internalNotes}</p>
            </div>
          )}
        </div>
      )}

      {/* Linked Invoices */}
      {so.invoices && so.invoices.length > 0 && (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-200 bg-gray-50 px-5 py-3 flex items-center gap-2">
            <FileText size={14} className="text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-700">Invoices ({so.invoices.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {so.invoices.map((inv: any) => (
              <div key={inv.id} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                    className="font-medium text-[var(--ink-0)] hover:underline text-sm"
                  >
                    {inv.number}
                  </button>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    inv.status === 'paid' ? 'bg-green-50 text-green-700'
                    : inv.status === 'sent' ? 'bg-blue-50 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                  }`}>
                    {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                  </span>
                  <span className="text-xs text-gray-500">{formatDate(inv.issueDate)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">{formatINR(Number(inv.total))}</span>
                  <a
                    href={`/api/v1/invoices/${inv.id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Download size={12} />
                    PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && <InvoiceModal so={so} onClose={() => setShowInvoiceModal(false)} onCreated={(inv) => {
        setShowInvoiceModal(false);
        api.get(`/sales-orders/${id}`).then(res => setSO(res.data.data));
        navigate(`/invoices/${inv.id}/edit`);
      }} />}
    </div>
  );
}

// ==================== INVOICE CREATION MODAL ====================

function InvoiceModal({ so, onClose, onCreated }: { so: any; onClose: () => void; onCreated: (inv: any) => void }) {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const q: Record<string, number> = {};
    so.items.forEach((item: any) => {
      const remaining = Number(item.quantity) - Number(item.invoicedQty);
      if (remaining > 0) q[item.id] = remaining;
    });
    return q;
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdInvoice, setCreatedInvoice] = useState<any>(null);

  const handleCreate = async () => {
    const items = Object.entries(quantities)
      .filter(([_, qty]) => qty > 0)
      .map(([salesOrderItemId, quantity]) => ({ salesOrderItemId, quantity }));

    if (items.length === 0) {
      setError('Select at least one item to invoice');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const res = await api.post(`/sales-orders/${so.id}/invoice`, { items });
      setCreatedInvoice(res.data.data);
      onCreated(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const eligibleItems = so.items.filter((item: any) => Number(item.quantity) - Number(item.invoicedQty) > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {createdInvoice ? (
          /* Success state */
          <div className="text-center">
            <CheckCircle2 size={48} className="mx-auto mb-3 text-green-500" />
            <h2 className="mb-1 text-lg font-bold text-gray-900">Invoice Created</h2>
            <p className="mb-6 text-sm text-gray-500">{createdInvoice.number}</p>
            <div className="flex flex-col gap-3">
              <a
                href={`/api/v1/invoices/${createdInvoice.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2.5 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
              >
                <Download size={14} />
                Download Invoice PDF
              </a>
              <button
                onClick={() => navigate(`/invoices/${createdInvoice.id}/edit`)}
                className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FileText size={14} />
                Open Invoice Editor
              </button>
              <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600">
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="mb-4 text-lg font-bold text-gray-900">Create Invoice from {so.number}</h2>

            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            {eligibleItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">All items have been fully invoiced</p>
            ) : (
              <div className="space-y-3">
                {eligibleItems.map((item: any) => {
                  const remaining = Number(item.quantity) - Number(item.invoicedQty);
                  return (
                    <div key={item.id} className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.description}</p>
                        <p className="text-xs text-gray-500">Remaining: {remaining} {item.unit || 'units'} @ {formatINR(Number(item.rate))}</p>
                      </div>
                      <input
                        type="number"
                        min={0}
                        max={remaining}
                        value={quantities[item.id] || 0}
                        onChange={e => setQuantities({ ...quantities, [item.id]: Math.min(parseFloat(e.target.value) || 0, remaining) })}
                        className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-right text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              {eligibleItems.length > 0 && (
                <button onClick={handleCreate} disabled={creating}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  Create Invoice
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
